import { useEffect, useRef, useState } from "react";
import stringSimilarity from "string-similarity";
import VoiceRecognitionBar from "../design/VoiceRecognitionBar";
import axios from "axios";
import { useRecoilState } from "recoil";
import { readingProblemTypeAtom } from "../atoms/readingProblemTypeAtom";
import { base64ToBlob } from "./ReadingMain";

interface GrammerQuesType {
  idx: number;
  grammer: string;
  question: string;
  answer: string;
}

const GrammerQues = ({
  listening,
  transcript,
  resetTranscript,
  toggleListening,
  setCurSentence,
  sentence,
  sentenceLenght,
}: {
  listening: boolean;
  transcript: string;
  resetTranscript: () => void;
  toggleListening: () => void;
  setCurSentence: React.Dispatch<React.SetStateAction<number>>;
  sentence: string;
  sentenceLenght: number;
}) => {
  const [quesNum, setQuesNum] = useState(0);
  const [isVoiceRecognitionRight, setIsVoiceRecognitionRight] = useState(0); // 0 아무텍스트도 없음, 1 잘하셨어요 -1 다시한번 생각해보세요
  const [grammerQues, setGrammerQues] = useState<GrammerQuesType[]>([]);
  const [problemType, setProblemType] = useRecoilState(readingProblemTypeAtom); //0:grammer, 1:noun, 2:emoji
  const [
    numberOfIncorrectGrammarQuestions,
    setNumberOfIncorrectGrammarQuestions,
  ] = useState(0);
  const targetQues = grammerQues[quesNum];
  const numberOfQue = grammerQues.length;
  const [queAnswer, setQueAnswer] = useState("");

  const audioElement = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    makeGrammerQues();
  }, [sentence]);

  const makeGrammerQues = async () => {
    const res = await axios.get(
      "https://uvlqtxcfrh.us14.qoddiapp.com/reading/ques/grammer",
      { params: { sentence } }
    );
    const audioRes = await axios.get(
      "https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts",
      { params: { ssml: `<speak>${res.data.output[0].question}</speak>` } }
    );

    const { audioContent } = audioRes.data;
    const audioBlob = base64ToBlob(audioContent, "audio/mp3");
    const audioUrl = URL.createObjectURL(audioBlob);

    // 오디오 요소 생성 및 바로 실행
    audioElement.current = new Audio(audioUrl);
    audioElement.current.play(); // 바로 오디오 실행

    setGrammerQues(res.data.output);
  };

  const onSubmitTranscript = () => {
    const targetSentence = targetQues.answer;
    setQueAnswer(targetSentence);
    const similarity = stringSimilarity.compareTwoStrings(
      targetSentence,
      transcript
    );
    if (listening) {
      toggleListening(); // 음성 인식을 멈춤
      setTimeout(() => resetTranscript(), 200); // 약간의 지연 후 초기화
    } else {
      resetTranscript();
    }
    setIsVoiceRecognitionRight(0);
    if (similarity >= 0.5) {
      setIsVoiceRecognitionRight(1);
      setNumberOfIncorrectGrammarQuestions(0);
      if (quesNum === numberOfQue - 1) {
        //한문장에 대해 문제를 다 순회하면
        setQuesNum(0); // 문제 번호를 0으로 초기화 (한 문장당 문제가 여러개 있음)
        setProblemType(1); // 그리고 다음 문제 단계로 넘어감
      } else {
        setQuesNum((prev) => prev + 1);
      }
    } else {
      setNumberOfIncorrectGrammarQuestions((prev) => prev + 1);
      if (numberOfIncorrectGrammarQuestions == 2) {
        if (quesNum === numberOfQue - 1) {
          //한문장에 대해 문제를 다 순회하면
          setQuesNum(0); // 문제 번호를 0으로 초기화 (한 문장당 문제가 여러개 있음)
          setProblemType(1); // 그리고 다음 문제 단계로 넘어감
        } else {
          setQuesNum((prev) => prev + 1);
        }
        setNumberOfIncorrectGrammarQuestions(0);
      }
      setIsVoiceRecognitionRight(-1);
    }
  };

  useEffect(() => {
    if (targetQues) {
      axios
        .get("https://uvlqtxcfrh.us14.qoddiapp.com/reading/tts", {
          params: { ssml: `<speak>${targetQues.question}</speak>` },
        })
        .then((audioRes) => {
          const { audioContent } = audioRes.data;
          const audioBlob = base64ToBlob(audioContent, "audio/mp3");
          const audioUrl = URL.createObjectURL(audioBlob);

          // 오디오 요소 생성 및 바로 실행
          audioElement.current = new Audio(audioUrl);
          audioElement.current.play(); // 바로 오디오 실행
        });
    }
  }, [quesNum]);

  return (
    <div>
      {grammerQues.length !== 0 ? (
        <div>
          <div className="border border-slate-500 rounded-md">
            <div>
              {quesNum + 1} {targetQues.question}
            </div>
            <div>{targetQues.answer.replace(/[\uAC00-\uD7A3]/g, "□")}</div>
          </div>
          <div className="text-pink-500">
            {isVoiceRecognitionRight === 0
              ? ""
              : isVoiceRecognitionRight === 1
              ? "잘하셨어요!"
              : numberOfIncorrectGrammarQuestions === 2
              ? `힌트! ${queAnswer}`
              : "틀렸습니다."}
          </div>
          <VoiceRecognitionBar
            listening={listening}
            transcript={transcript}
            toggleListening={toggleListening}
            onSubmitTranscript={onSubmitTranscript}
          />
        </div>
      ) : (
        "그래머 문제 생성중..."
      )}
    </div>
  );
};

export default GrammerQues;
